import { useState } from "react";
import { motion } from "framer-motion";
import { ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, Users, Bot } from "lucide-react";
import { SCENARIOS, RECYCLING_ROWS, RECYCLING_TOTAL, WASTE_TONNES, MATERIAL_PRICES, BUYERS } from "@/data";
import ChapterMark from "@/components/ChapterMark";

const formatM = (v) => `$${v}m`;

function ChartTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-[#020D18] border border-[#1C3A54] rounded-lg px-4 py-3 text-sm shadow-xl">
            <p className="font-semibold text-slate-200 mb-1">{label}</p>
            {payload.map((entry) => (
                <p key={entry.dataKey} style={{ color: entry.color }}>
                    {entry.name}: ${entry.value}m
                </p>
            ))}
        </div>
    );
}

function TonnesTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-[#020D18] border border-[#1C3A54] rounded-lg px-4 py-3 text-sm shadow-xl">
            <p className="font-semibold text-slate-200 mb-1">{label}</p>
            {payload.map((entry) => (
                <p key={entry.dataKey} style={{ color: entry.color }}>
                    {entry.name}: {entry.value} t
                </p>
            ))}
        </div>
    );
}

export default function Financials() {
    const [scenario, setScenario] = useState("expected");
    const active = SCENARIOS[scenario];

    return (
        <section id="financials" className="relative overflow-hidden scroll-mt-24 py-24 md:py-32 bg-[#041222] border-y border-[#12283C]" data-testid="financials-section">
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.07]"
                style={{ backgroundImage: "url(/images/harbour-green-aerial.webp)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#041222]/70 via-transparent to-[#041222]/70" />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-3xl">
                    <ChapterMark index="08" label="The Business Case" testid="financials-eyebrow" />
                    <h2 className="font-display font-semibold tracking-tight text-4xl md:text-5xl" data-testid="financials-heading">
                        A financially self-sustaining restoration service
                    </h2>
                    <p className="mt-5 text-lg md:text-xl text-slate-400 leading-relaxed" data-testid="financials-intro">
                        RiverGuardian runs on annual council and utility contracts, not donations or grants.
                        The model stays lean: at maturity, 120 Guardians are serviced by a crew of about fifteen,
                        one for every two of the roughly thirty stations, and expansion spending stops once
                        Sydney is covered.
                    </p>
                </div>

                <div className="mt-10 flex flex-wrap gap-3" data-testid="scenario-tabs">
                    {Object.entries(SCENARIOS).map(([key, s]) => (
                        <button
                            key={key}
                            data-testid={`scenario-tab-${key}`}
                            onClick={() => setScenario(key)}
                            className={`px-5 py-2.5 rounded-full text-base font-semibold border transition-colors ${
                                scenario === key
                                    ? "bg-sky-400 text-[#020D18] border-sky-400"
                                    : "border-[#1C3A54] text-slate-300 hover:border-sky-300 hover:text-sky-200"
                            }`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
                <p className="mt-3 text-sm text-slate-500" data-testid="scenario-note">{active.note}</p>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 bg-[#06182A] border border-[#12283C] rounded-xl p-6" data-testid="financial-chart-card">
                        <p className="font-display font-semibold text-lg text-slate-200 mb-4">
                            Annual contract revenue vs cumulative operating profit ($m, {active.label.toLowerCase()} case)
                        </p>
                        <div className="h-[380px]" data-testid="financial-chart">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={active.data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                                    <CartesianGrid stroke="#12283C" strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="year" stroke="#5B7B86" tick={{ fontSize: 13 }} tickLine={false} axisLine={false} />
                                    <YAxis stroke="#5B7B86" tick={{ fontSize: 13 }} tickLine={false} axisLine={false} tickFormatter={formatM} />
                                    <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(52,211,153,0.08)" }} />
                                    <Legend wrapperStyle={{ fontSize: 13 }} />
                                    <Bar
                                        dataKey="revenue"
                                        name="Annual revenue"
                                        fill="#34D399"
                                        radius={[4, 4, 0, 0]}
                                        maxBarSize={36}
                                        isAnimationActive
                                        animationDuration={1200}
                                        animationEasing="ease-out"
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="cumulative"
                                        name="Cumulative operating profit"
                                        stroke="#38BDF8"
                                        strokeWidth={2.5}
                                        dot={{ r: 3, fill: "#38BDF8" }}
                                        isAnimationActive
                                        animationDuration={1400}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="flex flex-col gap-4" data-testid="financial-highlights">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="bg-[#06182A] border border-[#12283C] rounded-xl p-6 flex-1"
                            data-testid="highlight-profit"
                        >
                            <TrendingUp className="w-5 h-5 text-sky-200" />
                            <p className="mt-3 font-display font-semibold text-4xl text-sky-200">{active.headline}</p>
                            <p className="mt-2 text-base text-slate-400 leading-relaxed">
                                Cumulative operating profit by end of Year 10, before tax, interest and
                                depreciation.
                            </p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="bg-[#06182A] border border-[#12283C] rounded-xl p-6 flex-1"
                            data-testid="highlight-fleet"
                        >
                            <Bot className="w-5 h-5 text-sky-200" />
                            <p className="mt-3 font-display font-semibold text-4xl">120 Guardians</p>
                            <p className="mt-2 text-base text-slate-400 leading-relaxed">
                                Full Sydney fleet by Year 6. From then on we replace only ~12 units a year at
                                ~$7,500 each, roughly $90k annually.
                            </p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="bg-[#06182A] border border-[#12283C] rounded-xl p-6 flex-1"
                            data-testid="highlight-staff"
                        >
                            <Users className="w-5 h-5 text-sky-200" />
                            <p className="mt-3 font-display font-semibold text-4xl">1 per 2 stations</p>
                            <p className="mt-2 text-base text-slate-400 leading-relaxed">
                                Around fifteen crew, one for every two stations, hand the sorted catch to
                                recycling-company collectors when the Guardians dock, and make sure each unit
                                is charging properly. At least one in five crew are Indigenous Australian women.
                            </p>
                        </motion.div>
                    </div>
                </div>

                <div className="mt-12 bg-[#06182A] border border-[#12283C] rounded-xl p-6" data-testid="tonnes-chart-card">
                    <p className="font-display font-semibold text-lg text-slate-200 mb-1">
                        Waste pulled from the river, in tonnes
                    </p>
                    <p className="text-sm text-slate-500 mb-4">
                        Total rubbish collected vs material sold to recyclers each year (tonnes).
                    </p>
                    <div className="h-[320px]" data-testid="tonnes-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={WASTE_TONNES} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                                <CartesianGrid stroke="#12283C" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="year" stroke="#5B7B86" tick={{ fontSize: 13 }} tickLine={false} axisLine={false} />
                                <YAxis stroke="#5B7B86" tick={{ fontSize: 13 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v} t`} />
                                <Tooltip content={<TonnesTooltip />} cursor={{ fill: "rgba(56,189,248,0.08)" }} />
                                <Legend wrapperStyle={{ fontSize: 13 }} />
                                <Bar
                                    dataKey="collected"
                                    name="Rubbish collected (t)"
                                    fill="#38BDF8"
                                    radius={[4, 4, 0, 0]}
                                    maxBarSize={30}
                                    isAnimationActive
                                    animationDuration={1300}
                                    animationEasing="ease-out"
                                />
                                <Bar
                                    dataKey="sold"
                                    name="Material sold (t)"
                                    fill="#34D399"
                                    radius={[4, 4, 0, 0]}
                                    maxBarSize={30}
                                    isAnimationActive
                                    animationDuration={1500}
                                    animationEasing="ease-out"
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="mt-16" data-testid="recycling-block">
                    <h3 className="font-display font-semibold text-3xl" data-testid="recycling-heading">
                        A second revenue stream: the material itself
                    </h3>
                    <p className="mt-3 text-lg text-slate-400 max-w-3xl leading-relaxed">
                        Around 60% of collected material is recyclable. After allocating 25% of recyclables to
                        Indigenous artists, the remainder is sold, roughly $190–$192 of revenue for every tonne
                        of rubbish collected. This sits on top of council contract fees.
                    </p>

                    <div className="mt-8 overflow-x-auto border border-[#12283C] rounded-xl" data-testid="recycling-table-wrapper">
                        <table className="w-full text-base" data-testid="recycling-table">
                            <thead>
                                <tr className="bg-[#06182A] text-left text-slate-400">
                                    <th className="p-4 font-semibold">Year</th>
                                    <th className="p-4 font-semibold">Rubbish collected</th>
                                    <th className="p-4 font-semibold">Recyclable (60%)</th>
                                    <th className="p-4 font-semibold">To Indigenous artists (25%)</th>
                                    <th className="p-4 font-semibold">Material sold (75%)</th>
                                    <th className="p-4 font-semibold">Recycling revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {RECYCLING_ROWS.map((row) => (
                                    <tr key={row.year} className={row.year % 2 === 1 ? "bg-[#020D18]" : "bg-[#041222]"} data-testid={`recycling-row-${row.year}`}>
                                        <td className="p-4 text-slate-200 font-semibold">{row.year}</td>
                                        <td className="p-4 text-slate-300">{row.collected} t</td>
                                        <td className="p-4 text-slate-300">{row.recyclable} t</td>
                                        <td className="p-4 text-sky-200">{row.artists} t</td>
                                        <td className="p-4 text-slate-300">{row.sold} t</td>
                                        <td className="p-4 text-sky-200 font-semibold">{row.revenue}</td>
                                    </tr>
                                ))}
                                <tr className="bg-[#06182A] border-t border-[#1C3A54]" data-testid="recycling-total-row">
                                    <td className="p-4 font-bold text-slate-100">Total</td>
                                    <td className="p-4 font-bold text-slate-100">{RECYCLING_TOTAL.collected}</td>
                                    <td className="p-4 font-bold text-slate-100">{RECYCLING_TOTAL.recyclable}</td>
                                    <td className="p-4 font-bold text-sky-200">{RECYCLING_TOTAL.artists}</td>
                                    <td className="p-4 font-bold text-slate-100">{RECYCLING_TOTAL.sold}</td>
                                    <td className="p-4 font-bold text-sky-200">{RECYCLING_TOTAL.revenue}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-[#06182A] border border-[#12283C] rounded-xl p-6" data-testid="material-prices-card">
                            <p className="font-display font-semibold text-lg">Assumed material prices</p>
                            <div className="mt-4 flex flex-col gap-3">
                                {MATERIAL_PRICES.map((m, i) => (
                                    <div key={m.material} className="flex items-center justify-between text-base" data-testid={`material-price-${i}`}>
                                        <span className="text-slate-300">{m.material}</span>
                                        <span className="text-slate-400">
                                            <span className="text-sky-200 font-semibold">{m.price}</span> · {m.share} of recyclables
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="bg-[#06182A] border border-[#12283C] rounded-xl p-6" data-testid="buyers-card">
                            <p className="font-display font-semibold text-lg">Potential buyers</p>
                            <div className="mt-4 flex flex-col gap-3">
                                {BUYERS.map((b, i) => (
                                    <div key={b.buyer} className="text-base" data-testid={`buyer-${i}`}>
                                        <span className="text-slate-200 font-semibold">{b.buyer}</span>
                                        <span className="text-slate-500">, {b.material}: {b.why}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <p className="mt-6 text-sm text-slate-500 leading-relaxed" data-testid="financials-disclaimer">
                        Projections are an expected-case operating model, not a guarantee. Figures are before
                        tax, interest/finance costs and accounting depreciation. Actual recycling revenue depends
                        on sorting quality and market prices at time of sale. The largest uncertainty is whether
                        councils collectively sign ~$8m/year of contracts, hence the scenario range above.
                    </p>
                </div>
            </div>
        </section>
    );
}
